import { useEffect } from "react";
import { isDialogOpen } from "../state/dialogStore";
import { usePanelStore } from "../state/panelStore";
import { Kbd } from "./Kbd";
import { Tooltip } from "./Tooltip";
import type { DrawingToolId, IndicatorId } from "./chartVocabulary";

/**
 * The TA workbench, collapsed into one quiet Tools drawer (spec §8, GRA-83,
 * GRA-131). Arms a drawing tool (trendline / horizontal S-R / Fibonacci) and
 * toggles indicator overlays (moving average / EMA / Bollinger / RSI / MACD /
 * volume). Ids here are ours (chartVocabulary.ts); the Chart adapter is the
 * only place that translates them to KLineChart's names.
 *
 * Why a drawer and not the old always-open strip: ten controls sat above the
 * chart on every screen, and below `sm` the strip was hidden outright, so a
 * phone had no tools at all. Collapsed, the drawer is a single text trigger
 * that rides in the settings row, which leaves the chart and the decision the
 * two things on screen and gives touch players the tools back.
 *
 * Rendered as a fragment on purpose: the trigger is a flex item in the
 * settings strip's row, and the panel is a `basis-full` sibling that wraps
 * onto its own line under it. That keeps the whole drawer one component while
 * costing no extra row of chrome when it is closed.
 *
 * The hotkeys (T/H/F, Esc, and the indicator toggles M/E/B/X/A/V) are bound
 * here and stay bound whether the drawer is open or closed: a keyboard player
 * never has to open it. Closing the drawer only hides the controls; whatever
 * tool is armed stays armed, and whatever indicators are on stay on, which is
 * why the trigger and its summary name them.
 *
 * "W" toggles the drawer open/closed itself (GRA-148), alongside the settings
 * (",") and history/session ("l"/"s") shortcuts sharing the same utility row:
 * not T/H/F, since those already arm a drawing tool whether the drawer is
 * open or not. The open flag itself lives in the shared panelStore, so
 * opening this drawer closes whichever of the other three panels was open.
 */

/** Drawing tools (exotic tools deferred, spec §0). */
export const DRAWING_TOOLS: {
  id: DrawingToolId;
  label: string;
  hotkey: string;
  tooltip: string;
}[] = [
  { id: "line", label: "Trend", hotkey: "t", tooltip: "draw a trendline" },
  { id: "hline", label: "S/R", hotkey: "h", tooltip: "draw a support/resistance line" },
  { id: "fib", label: "Fib", hotkey: "f", tooltip: "draw a Fibonacci retracement" },
];

/** Indicator overlays. */
export const INDICATORS: { id: IndicatorId; label: string; hotkey: string; tooltip: string }[] = [
  { id: "sma", label: "MA", hotkey: "m", tooltip: "moving average" },
  { id: "ema", label: "EMA", hotkey: "e", tooltip: "exponential moving average" },
  { id: "bbands", label: "BOLL", hotkey: "b", tooltip: "Bollinger Bands" },
  { id: "rsi", label: "RSI", hotkey: "x", tooltip: "relative strength index" },
  { id: "macd", label: "MACD", hotkey: "a", tooltip: "moving average convergence/divergence" },
  { id: "vol", label: "VOL", hotkey: "v", tooltip: "volume" },
];

export type ToolsDrawerProps = {
  onTool: (toolId: DrawingToolId) => void;
  onClear: () => void;
  indicators: IndicatorId[];
  onToggleIndicator: (id: IndicatorId) => void;
  /** The tool currently armed and waiting for the player's clicks on the chart,
   *  or null. Drawing happens on the canvas, so without this the drawer was
   *  the one part of the screen that gave no sign a tool was live. */
  activeTool?: DrawingToolId | null;
  /**
   * Whether the current round carries volume worth a pane. The chart drops the
   * VOL pane on a round without it (GRA-133), so with this false the chip is
   * shown inert with a tooltip saying why, rather than as a live toggle that
   * does nothing when pressed (GRA-148). Defaults to true so a caller with no
   * round in hand (tests, the drawer on its own) gets the plain toggle.
   */
  volumeAvailable?: boolean;
};

/** Why the VOL chip is inert. */
const NO_VOLUME = "this round has no volume data";

function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return el?.tagName === "INPUT" || el?.tagName === "TEXTAREA";
}

/**
 * What the closed drawer still has to say: the armed tool, and how many
 * indicators are drawn. Without it the trigger would swallow the only sign
 * that a click on the chart is about to start a trendline.
 */
function DrawerSummary({
  activeTool,
  indicators,
  volumeAvailable,
}: {
  activeTool: DrawingToolId | null;
  indicators: IndicatorId[];
  volumeAvailable: boolean;
}) {
  const armed = DRAWING_TOOLS.find((tool) => tool.id === activeTool);
  // VOL is left out on a round with no volume: the chart is not drawing that
  // pane, so the closed drawer must not claim it is (GRA-148).
  const on = INDICATORS.filter(
    (indicator) => indicators.includes(indicator.id) && (indicator.id !== "vol" || volumeAvailable),
  );
  if (!armed && on.length === 0) return null;
  return (
    <span className="hidden min-w-0 items-center gap-2 text-xs sm:flex">
      {armed && <span className="text-accent">{armed.label}</span>}
      {on.length > 0 && (
        <span className="truncate text-dim">{on.map((i) => i.label).join(" ")}</span>
      )}
    </span>
  );
}

export function ToolsDrawer({
  onTool,
  onClear,
  indicators,
  onToggleIndicator,
  activeTool = null,
  volumeAvailable = true,
}: ToolsDrawerProps) {
  const open = usePanelStore((s) => s.open === "tools");
  const togglePanel = usePanelStore((s) => s.toggle);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      // The tool hotkeys and Esc-to-clear go quiet while a dialog is open
      // (GRA-146); the dialog's own Escape handler owns that key there.
      if (isDialogOpen()) return;
      if (isTypingTarget(event.target)) return;
      if (event.key === "Escape") {
        onClear();
        return;
      }
      // No modifier keys (matches the settings/history/session shortcuts,
      // GRA-148): otherwise a browser or OS chord ending in "w" would also
      // open this drawer.
      if (event.key.toLowerCase() === "w" && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        togglePanel("tools");
        return;
      }
      const tool = DRAWING_TOOLS.find((t) => t.hotkey === event.key.toLowerCase());
      if (tool) {
        event.preventDefault();
        onTool(tool.id);
        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const indicator = INDICATORS.find((i) => i.hotkey === event.key.toLowerCase());
      if (indicator) {
        // VOL stays inert on a round with no volume data, same as its click
        // guard in the render below (GRA-148): the hotkey must not fake a
        // toggle the chart isn't drawing.
        if (indicator.id === "vol" && !volumeAvailable) return;
        event.preventDefault();
        onToggleIndicator(indicator.id);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onTool, onClear, onToggleIndicator, volumeAvailable, togglePanel]);

  // Touch targets get the same 32px floor the settings chips use below `sm`,
  // since these controls are now reachable on a phone.
  const chip =
    "flex min-h-8 min-w-8 items-center justify-center rounded border px-2 py-1 text-xs transition-colors sm:min-h-6 sm:min-w-0";
  const off = "border-muted/30 text-muted hover:border-accent hover:text-accent";

  return (
    <>
      <button
        type="button"
        onClick={() => togglePanel("tools")}
        aria-expanded={open}
        className="flex min-h-8 items-center gap-2 text-xs text-muted hover:text-accent sm:min-h-6"
      >
        <span>{open ? "▾ tools" : "▸ tools"}</span>
        <Kbd>W</Kbd>
        {/* Only while closed: with the panel open the lit chips say the same
            thing one line below, and repeating it here is noise. */}
        {!open && (
          <DrawerSummary
            activeTool={activeTool}
            indicators={indicators}
            volumeAvailable={volumeAvailable}
          />
        )}
      </button>

      {open && (
        // `basis-full`: its own line under the strip's triggers, whatever else
        // shares that row. `order-last`: the history/session toggles come
        // after this panel in DOM order, so without it they would wrap onto a
        // third line beneath the open panel instead of staying in the row.
        <div className="order-last flex basis-full flex-wrap items-center gap-x-4 gap-y-1 py-2">
          <div className="flex flex-wrap items-center gap-1" aria-label="drawing tools">
            {DRAWING_TOOLS.map((tool) => {
              // Armed carries the same accent the indicator toggles use, so
              // "this control is on" reads the same way everywhere.
              const armed = activeTool === tool.id;
              return (
                <Tooltip key={tool.id} text={tool.tooltip} align="left" interactive>
                  <button
                    type="button"
                    aria-pressed={armed}
                    onClick={() => onTool(tool.id)}
                    className={`${chip} ${armed ? "border-accent text-accent" : off}`}
                  >
                    {tool.label}
                    <Kbd>{tool.hotkey.toUpperCase()}</Kbd>
                  </button>
                </Tooltip>
              );
            })}
            <Tooltip text="clear all drawings" align="left" interactive>
              <button type="button" onClick={onClear} className={`${chip} ${off}`}>
                Clear <Kbd>Esc</Kbd>
              </button>
            </Tooltip>
          </div>
          <div className="flex flex-wrap items-center gap-1" aria-label="indicators">
            {INDICATORS.map(({ id, label, hotkey, tooltip }) => {
              const on = indicators.includes(id);
              // Inert rather than hidden, and `aria-disabled` rather than
              // `disabled`, for the same reason the settings strip does it:
              // a chip that vanishes is harder to find again than one that
              // says why it is unavailable, and a truly disabled button
              // swallows the pointer events its tooltip needs.
              const inert = id === "vol" && !volumeAvailable;
              return (
                <Tooltip key={id} text={inert ? NO_VOLUME : tooltip} align="left" interactive>
                  <button
                    type="button"
                    aria-pressed={on && !inert}
                    aria-disabled={inert || undefined}
                    onClick={inert ? undefined : () => onToggleIndicator(id)}
                    className={`${chip} ${
                      inert
                        ? "cursor-not-allowed border-muted/20 text-muted/40"
                        : on
                        ? "border-accent text-accent"
                        : "border-muted/30 text-muted hover:text-fg"
                    }`}
                  >
                    {label}
                    {/* `aria-hidden`: a visual hint only, so the chip's
                        accessible name stays just the label, same as
                        MarketChip's own key hint. */}
                    <span aria-hidden="true">
                      <Kbd>{hotkey.toUpperCase()}</Kbd>
                    </span>
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
